import React, { useState } from 'react';
/**
 * Desktop Widgets
 * File system themed welcome screen
 */
import { motion } from 'framer-motion';
import { Folder, FileText, Image, Code, Sparkles } from 'lucide-react';
import { useTheme } from '../../../core/contexts/ThemeContext';
import { useWindows } from '../../../core/contexts/WindowContext';
export function DesktopWidgets() {
  const {
    getThemeClasses,
    theme: currentTheme
  } = useTheme();
  const {
    windows,
    openWindow
  } = useWindows();
  const theme = getThemeClasses();
  const isLightTheme = currentTheme === 'light';
  const isNeonTheme = currentTheme === 'neon';
  const isHighContrast = currentTheme === 'highContrast';
  // Only show if no windows are open
  if (windows.length > 0) {
    return null;
  }
  const apps = [{
    id: 'about',
    label: 'About Me',
    color: 'from-emerald-500 to-teal-500',
    delay: 0,
    description: 'Mijn verhaal'
  }, {
    id: 'studio',
    label: 'Studio',
    color: 'from-fuchsia-500 to-violet-500',
    delay: 0.1,
    description: 'AI Tools'
  }, {
    id: 'projects',
    label: 'Projects',
    color: 'from-blue-500 to-indigo-500',
    delay: 0.2,
    description: 'Mijn werk'
  }, {
    id: 'expertise',
    label: 'Expertise',
    color: 'from-purple-500 to-violet-500',
    delay: 0.3,
    description: 'Skills'
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5,
    delay: 0.2
  }} className="min-h-screen flex flex-col pointer-events-none px-6" data-id="element-898">
      {/* UNDER CONSTRUCTION RIBBON - ALTIJD ZICHTBAAR */}
      <motion.div initial={{
      y: -100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.5,
      type: 'spring',
      bounce: 0.4
    }} className="pointer-events-auto w-full py-4 mt-8 mb-4" data-id="element-899">
        <div className={`max-w-4xl mx-auto flex items-center justify-center gap-4 px-8 py-4 rounded-2xl border-2 shadow-2xl ${isLightTheme ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-400 shadow-amber-200' : isHighContrast ? 'bg-white border-black' : isNeonTheme ? 'bg-gradient-to-r from-amber-900/40 to-orange-900/40 border-amber-400 shadow-amber-500/50 backdrop-blur-xl' : 'bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-amber-400/50 shadow-amber-500/30 backdrop-blur-xl'}`} data-id="element-900">
          <motion.div animate={{
          rotate: [0, 15, -15, 0],
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} className="text-4xl" data-id="element-901">
            🚧
          </motion.div>
          <div className="flex-1 text-center" data-id="element-902">
            <div className={`text-xl md:text-2xl font-black mb-1 ${isLightTheme ? 'text-amber-900' : isHighContrast ? 'text-black' : 'text-amber-300'}`} data-id="element-903">
              PORTFOLIO IN ONTWIKKELING
            </div>
            <div className={`text-sm md:text-base font-medium ${isLightTheme ? 'text-amber-700' : isHighContrast ? 'text-gray-700' : 'text-amber-400/80'}`} data-id="element-904">
              Niet alle features zijn actief • Werk in uitvoering
            </div>
          </div>
          <motion.div animate={{
          opacity: [1, 0.3, 1],
          scale: [1, 1.2, 1]
        }} transition={{
          duration: 1.5,
          repeat: Infinity
        }} className="text-4xl" data-id="element-905">
            ⚠️
          </motion.div>
        </div>
      </motion.div>
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center pointer-events-auto" data-id="element-906">
        <div className="text-center space-y-12 max-w-4xl w-full" data-id="element-907">
          {/* Hero Section */}
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: 0.3,
          type: 'spring',
          bounce: 0.3
        }} className="space-y-6" data-id="element-908">
            {/* MVL Creative Logo */}
            <div className="relative inline-block" data-id="element-909">
              {/* Glow effect */}
              {!isHighContrast && <motion.div animate={{
              scale: [1, 1.2, 1],
              opacity: [0.4, 0.7, 0.4]
            }} transition={{
              duration: 3,
              repeat: Infinity
            }} className={`absolute inset-0 rounded-3xl blur-2xl ${isNeonTheme ? 'bg-gradient-to-br from-cyan-500 to-fuchsia-500' : 'bg-gradient-to-br from-emerald-500 to-teal-500'}`} data-id="element-910" />}
              {/* Logo container */}
              <motion.div animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className={`relative w-32 h-32 rounded-3xl overflow-hidden bg-white ${isHighContrast ? 'border-4 border-white' : isNeonTheme ? 'border-2 border-cyan-500/50 shadow-2xl shadow-cyan-500/50' : 'border-2 border-emerald-500/30 shadow-2xl shadow-emerald-500/30'}`} style={{
              backdropFilter: 'blur(20px)'
            }} data-id="element-911">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rGwPzEqbTtWGmo9zdFVp6V/Gemini_Generated_Image_z1lwq1z1lwq1z1lw-removebg-preview.png" alt="MVL Creative" className="w-full h-full object-contain p-4" data-id="element-912" />
                {/* Scan line effect */}
                {!isHighContrast && <motion.div animate={{
                y: ['0%', '100%']
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'linear'
              }} className={`absolute inset-0 ${isNeonTheme ? 'bg-gradient-to-b from-transparent via-cyan-400/20 to-transparent' : 'bg-gradient-to-b from-transparent via-emerald-400/20 to-transparent'}`} style={{
                height: '30%'
              }} data-id="element-913" />}
              </motion.div>
              {/* Rotating ring */}
              <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'linear'
            }} className={`absolute inset-0 rounded-3xl border-2 border-dashed ${isNeonTheme ? 'border-cyan-400/30' : 'border-emerald-400/30'}`} data-id="element-914" />
              {/* Corner markers */}
              {!isHighContrast && <>
                  <div className={`absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 ${isNeonTheme ? 'border-cyan-400' : 'border-emerald-400'}`} data-id="element-915" />
                  <div className={`absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 ${isNeonTheme ? 'border-fuchsia-400' : 'border-teal-400'}`} data-id="element-916" />
                  <div className={`absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 ${isNeonTheme ? 'border-fuchsia-400' : 'border-teal-400'}`} data-id="element-917" />
                  <div className={`absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 ${isNeonTheme ? 'border-cyan-400' : 'border-emerald-400'}`} data-id="element-918" />
                </>}
            </div>
            {/* Text */}
            <div className="space-y-4" data-id="element-919">
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className={`inline-block px-4 py-2 rounded-full text-xs font-medium ${isLightTheme ? 'bg-gray-100 text-gray-700' : isHighContrast ? 'bg-white text-black border-2 border-black' : isNeonTheme ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50' : 'bg-white/10 text-white/80 border border-white/20'}`} data-id="element-920">
                <span className="flex items-center gap-2" data-id="element-921">
                  <span className={`w-2 h-2 rounded-full ${isNeonTheme ? 'bg-cyan-400' : 'bg-emerald-400'} animate-pulse`} data-id="element-922" />
                  System Online
                </span>
              </motion.div>
              <h1 className={`text-4xl md:text-5xl font-bold ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-white' : 'text-white'}`} data-id="element-923">
                Mick van Latum
              </h1>
              <p className={`text-lg md:text-xl font-medium ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-300' : isNeonTheme ? 'text-cyan-300' : 'text-white/70'}`} data-id="element-924">
                AI Creative System Thinker
              </p>
            </div>
          </motion.div>
          {/* App Grid */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-925">
            {apps.map(app => <motion.button key={app.id} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.6 + app.delay,
            type: 'spring',
            bounce: 0.4
          }} whileHover={{
            scale: 1.05,
            y: -4
          }} whileTap={{
            scale: 0.95
          }} onClick={() => openWindow(app.id, app.label)} className={`group relative backdrop-blur-xl rounded-2xl p-6 border transition-all ${isLightTheme ? 'bg-white/80 border-gray-200 hover:border-gray-300 hover:shadow-lg' : isHighContrast ? 'bg-white border-4 border-black hover:border-gray-700' : isNeonTheme ? 'bg-black/80 border-fuchsia-500/30 hover:border-fuchsia-500 hover:shadow-2xl hover:shadow-fuchsia-500/20' : 'bg-white/5 border-white/10 hover:border-white/20 hover:shadow-xl hover:shadow-black/20'}`} data-id="element-926">
                {/* Icon */}
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br ${app.color} flex items-center justify-center group-hover:scale-110 transition-transform ${isNeonTheme ? 'shadow-lg shadow-fuchsia-500/30' : 'shadow-lg shadow-black/10'}`} data-id="element-927">
                  <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-white" data-id="element-928">
                    {app.id === 'about' && <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" data-id="element-929" />}
                    {app.id === 'studio' && <>
                        <circle cx="12" cy="12" r="3" fill="currentColor" data-id="element-930" />
                        <path d="M12 2L14 8L20 10L14 12L12 18L10 12L4 10L10 8L12 2Z" fill="currentColor" data-id="element-931" />
                      </>}
                    {app.id === 'projects' && <path d="M3 3H10L12 5H21V19H3V3Z M3 7H21 M7 11H17 M7 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-932" />}
                    {app.id === 'expertise' && <>
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" data-id="element-933" />
                        <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-934" />
                      </>}
                  </svg>
                </div>
                {/* Label */}
                <div className={`font-semibold mb-1 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-935">
                  {app.label}
                </div>
                {/* Description */}
                <div className={`text-xs ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400/60' : 'text-white/40'}`} data-id="element-936">
                  {app.description}
                </div>
                {/* Hover Glow */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${app.color} opacity-0 group-hover:opacity-5 transition-opacity pointer-events-none`} data-id="element-937" />
              </motion.button>)}
          </motion.div>
          {/* CTA */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1
        }} className={`text-sm ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-400' : isNeonTheme ? 'text-cyan-400/50' : 'text-white/30'}`} data-id="element-938">
            Klik op een app om te beginnen →
          </motion.div>
        </div>
      </div>
    </motion.div>;
}